import { FormGroup, FormLabel, Button, FormControl, Spinner } from "react-bootstrap";
import SignatureCanvas from 'react-signature-canvas';
import { useRef, useState, useEffect } from 'react';
import { PassFailSelect } from "../components/PassFailSelect";
import { Photo } from "../models/Photo";
import { SimpleFormField } from "../components/SimpleFormField";
import { ButtonValueSelect } from "../components/ButtonValueSelect";
import { SpinnerButton } from "../components/SpinnerButton";
import { afterRenderDelay, blobToDataUrl, getRandomId } from "../util/utils";
import { CameraPhotoList, CameraToggleButton } from "../components/CameraPhoto";
import { isoTimestamp } from "../util/utils";

let passDispositionOptions = [
	["Pass", "PASS", "success", "secondary"],
	["Repair", "REPAIR", "warning", "secondary"]
];
let failDispositionOptions = [
	["Repair", "REPAIR", "warning", "secondary"],
	["Replace", "REPLACE", "danger", "secondary"]
];

// NOTE - onComplete is required and required to be async
export function FinalizationForm(props) {
	let { job, inspection, survey, onComplete, saveAction, onFinalFormChange } = props;
	let [showSignature, setShowSignature] = useState(false);

	let [inspectionResult, setInspectionResult] = useState(inspection.result);
	let [inspectionDisposition, setInspectionDisposition] = useState(inspection.disposition);
	let [inspectionRentalDisposition, setInspectionRentalDisposition] = useState(inspection.rentalDisposition);
	let dispositionOptions = inspectionResult == "PASS" ? passDispositionOptions : failDispositionOptions;

	let [isCompleting, setIsCompleting] = useState(false);
	let readyForCompletionBasic = (!inspection.rentalMode || !!inspectionRentalDisposition) && !!inspectionResult && !!inspectionDisposition;

	let [isCameraActive, setIsCameraActive] = useState(false);
	let [inspectionPhotos, setInspectionPhotos] = useState(inspection.inspectionPhotos);

	// State
	let sigRef = useRef();

	// Controller Actions
    let updateSignature = () => {
		inspection.signature = new Photo({
			photoData: sigRef.current.toDataURL()
		});
		saveAction?.();
    };

	let completeAction = (evt) => {
		setIsCompleting(true);
		let errors = inspection.getInspectionErrors(survey, job);
		if(errors.length > 0) {
			setIsCompleting(false);
			alert(`Could not complete inspection: ${errors.join("; ")}`);
			return;
		}
		
		afterRenderDelay(async () => { // Flush rendering pipeline for spinner
			await onComplete(); // NOTE - required to be async
			setIsCompleting(false);
		});
	};	

	let receivePhotoDataAction = (dataUri) => {
		let photo = new Photo({
			id: getRandomId(),
			photoData: dataUri
		});
		inspection.inspectionPhotos = [...inspection.inspectionPhotos, photo];
		setInspectionPhotos(inspection.inspectionPhotos);
		saveAction?.();
	};

	let deletePhotoAction = (ph) => () => {
		if(confirm("Are you sure you want to delete this photo?")) {
			inspection.inspectionPhotos = inspection.inspectionPhotos.filter(x => x.id != ph.id);
			setInspectionPhotos(inspection.inspectionPhotos);
			saveAction?.();
		}
	};

    useEffect(async () => {
        if(inspection.signature != null && sigRef.current != null) {
            console.log("Loading data URL");
			let sig = inspection.signature;
			if(sig.url == null) {
				sigRef.current.fromDataURL(inspection.signature.photoData);
			} else {
				// NOTE - requires CORS to be setup on the S3 bucket
				// https://docs.aws.amazon.com/AmazonS3/latest/userguide/enabling-cors-examples.html
				let resp = await fetch(sig.url);
				let blob = await resp.blob();
				let dataUrl = await blobToDataUrl(blob);
				sigRef.current.fromDataURL(dataUrl);
			}
        }
    })

	let updateFinalComments = (evt) => {
		inspection.comments = evt.target.value;
	}

	let rentalOptions = [
		["Check-In", "CHECK_IN", "info", "secondary"],
		["Check-Out", "CHECK_OUT", "warning", "secondary"]
	];

	let color = "#007041";

	return (
		<div className="finalForm">
			{inspection.rentalMode && <FormGroup>
				<FormLabel>Check In/Out</FormLabel>
				<ButtonValueSelect className="mx-4" bindObject={inspection} bindAttribute="rentalDisposition" options={rentalOptions} onChange={() => {setInspectionRentalDisposition(inspection.rentalDisposition); onFinalFormChange() } }/>
			</FormGroup>}

			<SimpleFormField color={color} label="Inspection Sticker" bindObject={inspection} bindAttribute="externalIdentifier" scannable={true} />
			<FormGroup className="mt-4">
				<FormLabel style={{color:"#007041"}}>Final Comments</FormLabel>
				<FormControl as="textarea" rows={3} defaultValue={inspection.comments} onChange={updateFinalComments}></FormControl>
			</FormGroup>
			<FormGroup className="mt-4">
				<FormLabel>General Photos <span style={{position: "relative", top:"-0.7em"}}><CameraToggleButton isCameraActive={isCameraActive} setIsCameraActive={setIsCameraActive} /></span></FormLabel>
				<CameraPhotoList isCameraActive={isCameraActive} receivePhotoDataAction={receivePhotoDataAction} deletePhotoAction={deletePhotoAction} photoList={inspectionPhotos} />
			</FormGroup>
			<FormGroup className="mt-4">
				<div className="finalTab">
					<FormLabel style={{color:"#007041"}} className="me-4 first">Final Result</FormLabel>
					<PassFailSelect bindObject={inspection} bindAttribute="result" onChange={() => {setInspectionResult(inspection.result); inspection.disposition = null; setInspectionDisposition(null); inspection.completedAt = isoTimestamp(); onFinalFormChange() }} />
				</div>
				</FormGroup>
			<FormGroup className="mt-4 finalTab">
				<FormLabel style={{color:"#007041"}} className="me-4 first">Final Disposition</FormLabel>
				<div>
					{!!(inspection.result) && <ButtonValueSelect bindObject={inspection} bindAttribute="disposition" options={dispositionOptions} onChange={() => {setInspectionDisposition(inspection.disposition); inspection.completedAt = isoTimestamp(); onFinalFormChange() }} />}
					{!(inspection.result) && <span >Pending...</span>}
				</div>
			</FormGroup>
			<FormGroup className="mt-4">
				{/* May want to move this to a modal - https://github.com/szimek/signature_pad/issues/496 */}
				<div className="finalTab">
					<FormLabel className="first" style={{color:"#007041"}}>Signature</FormLabel>
					<button className="mx-4 button" onClick={() => {setShowSignature(!showSignature)}}>{showSignature ? "Hide Signature" : "Show Signature"}</button>
				</div>
				{showSignature && <div className="border border-dark mt-2">
					<SignatureCanvas clearOnResize={false} ref={sigRef} onEnd={updateSignature} canvasProps={{className:"signature-block"}}></SignatureCanvas>
				</div>}	
				<br />
				
			</FormGroup>
			<FormGroup className="mt-4" style={{width:"100"}}>
				{/* <FormLabel style={{color:"#007041"}}>Complete</FormLabel>
				<br /> */}
				{/* <SpinnerButton className="finalTabComplete" onClick={completeAction} disabled={(!readyForCompletionBasic)} spinning={isCompleting} spinnerPosition="outside">Complete Inspection</SpinnerButton> */}
			</FormGroup>
			<SpinnerButton className="buttonFullWidth" spinning={isCompleting} onClick={completeAction} disabled={(!readyForCompletionBasic)}>Complete Inspection</SpinnerButton>
		</div>
	);
}